import { useState } from 'react';
import Button from '../../../shared/components/Button';
import { Input } from '../../../shared/components/Input';
import { usePermissions, Permission } from '../../../shared/hooks/usePermissions';
import {
  useFeatureFlags,
  useFeatureFlagAuditLog,
  useDeleteFeatureFlag,
  useUpdateFeatureFlag,
} from '../hooks/useFeatureFlags';
import { FeatureFlagModal } from '../components/FeatureFlagModal';
import { describeRollout, describeScope } from '../rollout';
import { FeatureFlagScope, type FeatureFlag } from '../types';

const inputClass =
  'px-3 border border-[#D9D9D9] bg-muted text-foreground rounded-[10px] h-[30px] text-[14px] focus:outline-none';

const formatDate = (iso: string) =>
  new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });

/** Audit history for one flag, expanded inline under its row. */
const AuditPanel = ({ flagId }: { flagId: string }) => {
  const { data: entries = [], isLoading } = useFeatureFlagAuditLog(flagId);

  if (isLoading) return <p className="text-sm text-muted-foreground">Loading history…</p>;
  if (!entries.length) return <p className="text-sm text-muted-foreground">No history yet.</p>;

  return (
    <ul className="space-y-2">
      {entries.map((entry) => (
        <li key={entry.id} className="text-sm text-foreground">
          <span className="font-semibold">{entry.action}</span>{' '}
          <span className="text-muted-foreground">
            {/* actorEmail is nullable in the audit row — fall back to the id
                rather than rendering an empty "by ". */}
            by {entry.actorEmail ?? entry.actorId} · {formatDate(entry.createdAt)}
          </span>
          {entry.payload && (
            <pre className="mt-1 text-xs bg-muted rounded p-2 overflow-x-auto">
              {JSON.stringify(entry.payload, null, 2)}
            </pre>
          )}
        </li>
      ))}
    </ul>
  );
};

/**
 * Feature flags (PE-2161, backend PE-2160).
 *
 * TCS-staff only — osprey-api's controller carries CollectiveOnlyGuard on top of
 * FEATURE_FLAG.READ/MANAGE. Unlike the campaign and intelligence-report surfaces,
 * the guard and the watchman grant agree here: watchman seeds FEATURE_FLAG to
 * COLLECTIVE_SUPERADMIN and COLLECTIVE_ADMIN only, so no collective-only shim
 * constant is needed.
 */
export const FeatureFlagsPage = () => {
  const { hasPermission } = usePermissions();
  const canManage = hasPermission(Permission.FEATURE_FLAG_MANAGE);

  const [keyFilter, setKeyFilter] = useState('');
  const [scopeFilter, setScopeFilter] = useState<'' | FeatureFlagScope>('');
  const [enabledFilter, setEnabledFilter] = useState<'' | 'true' | 'false'>('');

  const { data: flags = [], isLoading, error } = useFeatureFlags({
    key: keyFilter.trim() || undefined,
    scope: scopeFilter || undefined,
    enabled: enabledFilter === '' ? undefined : enabledFilter === 'true',
  });

  const [editing, setEditing] = useState<FeatureFlag | undefined>();
  const [creating, setCreating] = useState(false);
  const [expandedAudit, setExpandedAudit] = useState<string | undefined>();
  const [deleting, setDeleting] = useState<FeatureFlag | undefined>();

  const updateMutation = useUpdateFeatureFlag();
  const deleteMutation = useDeleteFeatureFlag();

  return (
    <div className="p-4 sm:p-6 space-y-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Feature Flags</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Turn platform features on per solution or merchant. The most specific row wins —
            merchant over solution over global.
          </p>
        </div>
        {canManage && <Button onClick={() => setCreating(true)}>New flag</Button>}
      </div>

      <div className="flex flex-wrap gap-3 items-end">
        <Input
          name="keyFilter"
          label="Key"
          value={keyFilter}
          onChange={(e) => setKeyFilter(e.target.value)}
          placeholder="Filter by key"
          className="w-48"
        />
        <label className="block text-[14px] font-[600] text-foreground">
          Scope
          <select
            className={`${inputClass} mt-1 block`}
            value={scopeFilter}
            onChange={(e) => setScopeFilter(e.target.value as '' | FeatureFlagScope)}>
            <option value="">All</option>
            <option value={FeatureFlagScope.GLOBAL}>Global</option>
            <option value={FeatureFlagScope.SOLUTION}>Solution</option>
            <option value={FeatureFlagScope.MERCHANT}>Merchant</option>
          </select>
        </label>
        <label className="block text-[14px] font-[600] text-foreground">
          State
          <select
            className={`${inputClass} mt-1 block`}
            value={enabledFilter}
            onChange={(e) => setEnabledFilter(e.target.value as '' | 'true' | 'false')}>
            <option value="">All</option>
            <option value="true">Enabled</option>
            <option value="false">Disabled</option>
          </select>
        </label>
      </div>

      {error && <p className="text-sm text-destructive">Could not load feature flags.</p>}
      {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}

      {!isLoading && !flags.length && (
        <p className="text-sm text-muted-foreground">No flags match these filters.</p>
      )}

      <div className="space-y-3">
        {flags.map((flag) => (
          <div key={flag.id} className="rounded-lg border border-border bg-card p-4 space-y-3">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="flex items-center gap-2">
                  <code className="font-mono font-semibold text-foreground">{flag.key}</code>
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full ${
                      flag.enabled ? 'bg-green-100 text-green-800' : 'bg-muted text-muted-foreground'
                    }`}>
                    {flag.enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
                {flag.description && (
                  <p className="text-sm text-muted-foreground mt-1">{flag.description}</p>
                )}
                <p className="text-xs text-muted-foreground mt-1">
                  {describeScope(flag)} · {flag.type} · {describeRollout(flag)}
                </p>
              </div>

              <div className="flex items-center gap-3">
                {canManage && (
                  <button
                    onClick={() =>
                      updateMutation.mutate({ id: flag.id, payload: { enabled: !flag.enabled } })
                    }
                    disabled={updateMutation.isPending}
                    className="text-sm text-blue-600 hover:underline disabled:opacity-50">
                    {flag.enabled ? 'Disable' : 'Enable'}
                  </button>
                )}
                <button
                  onClick={() => setExpandedAudit(expandedAudit === flag.id ? undefined : flag.id)}
                  className="text-sm text-blue-600 hover:underline">
                  History
                </button>
                {canManage && (
                  <>
                    <button
                      onClick={() => setEditing(flag)}
                      className="text-sm text-blue-600 hover:underline">
                      Edit
                    </button>
                    <button
                      onClick={() => setDeleting(flag)}
                      className="text-sm text-red-600 hover:underline">
                      Delete
                    </button>
                  </>
                )}
              </div>
            </div>

            {flag.variants?.length ? (
              <ul className="flex flex-wrap gap-2">
                {flag.variants.map((variant) => (
                  <li
                    key={variant.key}
                    className="text-xs font-mono bg-muted rounded px-2 py-1 text-foreground">
                    {variant.key} · {variant.weight}
                  </li>
                ))}
              </ul>
            ) : null}

            {expandedAudit === flag.id && (
              <div className="border-t border-border pt-3">
                <AuditPanel flagId={flag.id} />
              </div>
            )}
          </div>
        ))}
      </div>

      {creating && <FeatureFlagModal onClose={() => setCreating(false)} />}
      {editing && <FeatureFlagModal flag={editing} onClose={() => setEditing(undefined)} />}

      {deleting && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-card rounded-lg w-full max-w-sm p-6 space-y-4">
            <h2 className="text-lg font-bold text-foreground">Delete this flag?</h2>
            <p className="text-sm text-foreground">
              <code className="font-mono">{deleting.key}</code> ({describeScope(deleting)}) will be
              removed. Anything evaluating it falls back to the next-broadest row, or to disabled if
              there is none.
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setDeleting(undefined)}
                className="px-4 py-2 text-sm font-medium text-foreground border border-border rounded-md hover:bg-muted">
                Cancel
              </button>
              <button
                type="button"
                onClick={async () => {
                  await deleteMutation.mutateAsync(deleting.id);
                  setDeleting(undefined);
                }}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 text-sm font-semibold bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50">
                {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
